import type { ApiClient } from "../auth/apiClient";
import {
    QueuePriorityPolicyResponseSchema,
    TaskTypeWeightsResponseSchema,
} from "./types";
import type {
    QueuePriorityAssignment,
    QueuePriorityOverride,
    QueuePriorityPolicyResponse,
    TaskTypeWeightsResponse,
} from "./types";

// Both stores expose only GET (read) + PUT (full replace) under
// /next/*; there is no PATCH/POST/DELETE. The PUT bodies are the whole
// desired state, mirroring the server's full-replace semantics — partial
// edits are reconciled client-side before the write.

export async function fetchQueuePriorityPolicy(
    client: ApiClient,
): Promise<QueuePriorityPolicyResponse> {
    const response = await client.fetch("/next/queue-priority-policy");
    if (!response.ok) {
        throw new Error(
            `Failed to fetch queue priority policy: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return QueuePriorityPolicyResponseSchema.parse(json);
}

export interface UpdateQueuePriorityPolicyInput {
    readonly personalQueueDefault: QueuePriorityAssignment;
    readonly overrides: readonly QueuePriorityOverride[];
}

export async function updateQueuePriorityPolicy(
    client: ApiClient,
    input: UpdateQueuePriorityPolicyInput,
): Promise<QueuePriorityPolicyResponse> {
    const response = await client.fetch("/next/queue-priority-policy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            personalQueueDefault: input.personalQueueDefault,
            overrides: input.overrides,
        }),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to update queue priority policy: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return QueuePriorityPolicyResponseSchema.parse(json);
}

export async function fetchTaskTypeWeights(
    client: ApiClient,
): Promise<TaskTypeWeightsResponse> {
    const response = await client.fetch("/next/task-type-weights");
    if (!response.ok) {
        throw new Error(
            `Failed to fetch task-type weights: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TaskTypeWeightsResponseSchema.parse(json);
}

export interface UpdateTaskTypeWeightsInput {
    /**
     * Full desired weight map (task-type wire value → positive multiplier).
     * Any known type omitted here is reset to the neutral weight server-side,
     * so the caller sends only the non-neutral rows it wants to persist.
     */
    readonly weights: Readonly<Record<string, number>>;
}

export async function updateTaskTypeWeights(
    client: ApiClient,
    input: UpdateTaskTypeWeightsInput,
): Promise<TaskTypeWeightsResponse> {
    const response = await client.fetch("/next/task-type-weights", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weights: input.weights }),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to update task-type weights: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TaskTypeWeightsResponseSchema.parse(json);
}
